<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import HistoryAPI from 'dashboard/api/history';
import AuditLogsAPI from 'dashboard/api/auditLogs';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useTracking } from 'dashboard/composables/useTracking';
import {
  LAIS_PAGE_PADDING_CLASS,
  LAIS_PAGE_SCROLL_SHELL_CLASS,
} from 'dashboard/helper/laisClassNames';

const loading = ref(false);
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const { track } = useTracking();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const routeNumber = (value, fallback) => {
  const parsed = Number(routeString(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const normalizeView = value =>
  value === 'auditoria' && isAdmin.value ? 'auditoria' : 'operacional';
const activeView = ref(normalizeView(routeString(route.query.view)));
const filters = ref({
  start_date: routeString(route.query.start_date) || '',
  end_date: routeString(route.query.end_date) || '',
  transaction_kind: routeString(route.query.transaction_kind) || '',
  public_kind: routeString(route.query.public_kind) || '',
  q: routeString(route.query.q) || '',
  limit: routeNumber(route.query.limit, 30),
});
const auditLoading = ref(false);
const auditExporting = ref(false);
const selectedAudit = ref(null);
const auditFilters = ref({
  start_date:
    activeView.value === 'auditoria'
      ? routeString(route.query.start_date) || ''
      : '',
  end_date:
    activeView.value === 'auditoria'
      ? routeString(route.query.end_date) || ''
      : '',
  actor_id: routeString(route.query.actor_id) || '',
  action_type: routeString(route.query.action_type) || '',
  q: activeView.value === 'auditoria' ? routeString(route.query.q) || '' : '',
  page: routeNumber(route.query.page, 1),
});
const auditPayload = ref({
  audit_logs_enabled: true,
  audit_logs: [],
  current_page: 1,
  total_entries: 0,
  per_page: 15,
});
const agents = computed(() => getters['agents/getAgents'].value || []);
const agentsById = computed(() =>
  Object.fromEntries(agents.value.map(agent => [agent.id, agent]))
);

const payload = ref({
  periodo: { label: '-' },
  cards: {
    saldo: null,
    receita: null,
    despesa: null,
    comissao: null,
  },
  transacoes: [],
  historico_publico: [],
  atividades: [],
  resumo_publico: {
    total: 0,
    tickets: 0,
    feedback_suporte: 0,
    sessoes_responde: 0,
    visitas_publicas: 0,
    incidentes_status: 0,
  },
});

const CARD_LABELS = {
  saldo: 'Saldo',
  receita: 'Receita',
  despesa: 'Despesa',
  comissao: 'Comissão',
};

const STATUS = {
  pago: { label: 'Pago', cls: 'bg-emerald-50 text-emerald-700' },
  aberto: { label: 'Em aberto', cls: 'bg-amber-50 text-amber-700' },
  processado: { label: 'Processado', cls: 'bg-sky-50 text-sky-700' },
  submitted: { label: 'Enviado', cls: 'bg-sky-50 text-sky-700' },
  in_progress: { label: 'Em andamento', cls: 'bg-amber-50 text-amber-700' },
  waiting_on_customer: {
    label: 'Aguardando cliente',
    cls: 'bg-amber-50 text-amber-700',
  },
  resolved: { label: 'Resolvido', cls: 'bg-emerald-50 text-emerald-700' },
  active: { label: 'Ativa', cls: 'bg-emerald-50 text-emerald-700' },
  reset: { label: 'Reiniciada', cls: 'bg-slate-100 text-slate-700' },
  pending: { label: 'Pendente', cls: 'bg-amber-50 text-amber-700' },
  confirmed: { label: 'Confirmada', cls: 'bg-emerald-50 text-emerald-700' },
  declined: { label: 'Recusada', cls: 'bg-rose-50 text-rose-700' },
  investigating: { label: 'Investigando', cls: 'bg-rose-50 text-rose-700' },
  monitoring: { label: 'Monitorando', cls: 'bg-amber-50 text-amber-700' },
  failed: { label: 'Falhou', cls: 'bg-rose-50 text-rose-700' },
  cancelled: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-700' },
};

const KIND = {
  receita: { label: 'Receita', cls: 'bg-emerald-50 text-emerald-700' },
  despesa: { label: 'Despesa', cls: 'bg-rose-50 text-rose-700' },
  comissao: { label: 'Comissão', cls: 'bg-violet-50 text-violet-700' },
  ticket: { label: 'Suporte', cls: 'bg-sky-50 text-sky-700' },
  feedback: { label: 'Feedback', cls: 'bg-slate-100 text-slate-700' },
  responde: { label: 'Responde', cls: 'bg-violet-50 text-violet-700' },
  visita: { label: 'Visita', cls: 'bg-amber-50 text-amber-700' },
  status: { label: 'Status', cls: 'bg-slate-100 text-slate-700' },
};

const ACTION_META = {
  create: { label: 'Criação', cls: 'bg-emerald-50 text-emerald-700' },
  update: { label: 'Atualização', cls: 'bg-amber-50 text-amber-700' },
  destroy: { label: 'Exclusão', cls: 'bg-rose-50 text-rose-700' },
};

const AUDITABLE_LABELS = {
  Account: 'Conta',
  AccountUser: 'Membro',
  Contact: 'Contato/Lead',
  Conversation: 'Conversa',
  Inbox: 'Caixa de entrada',
  InboxMember: 'Membro da caixa',
  Team: 'Equipe',
  TeamMember: 'Membro da equipe',
  User: 'Usuário',
  Webhook: 'Webhook',
  AutomationRule: 'Automação',
  Macro: 'Macro',
  Imovel: 'Imóvel',
  Visita: 'Visita',
  PropertyOffer: 'Oferta',
};

const SENSITIVE_AUDIT_FIELDS = [
  'encrypted_password',
  'password',
  'token',
  'access_token',
  'refresh_token',
  'pubsub_token',
  'secret',
];

const formatBRL = value =>
  value == null
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value));

const formatDate = value => {
  if (!value) return '-';
  const parsed = new Date(
    Number(value) < 1_000_000_000_000 ? Number(value) * 1000 : value
  );
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
};

const formatCount = (value, singular, plural) =>
  `${value} ${Number(value) === 1 ? singular : plural}`;

const cardLabel = key => CARD_LABELS[key] || String(key).replaceAll('_', ' ');

const statusMeta = status =>
  STATUS[status] || {
    label: status ? String(status).replaceAll('_', ' ') : 'Indisponível',
    cls: 'bg-slate-100 text-slate-700',
  };

const kindMeta = kind =>
  KIND[kind] || {
    label: kind || 'Evento',
    cls: 'bg-slate-100 text-slate-700',
  };

const cleanParams = () =>
  Object.fromEntries(
    Object.entries(filters.value).filter(([, value]) => value !== '')
  );
const cleanObject = object =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== '' && value != null)
  );

const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

const syncHistoryRouteState = () => {
  const nextQuery = compactQuery({
    ...route.query,
    view: activeView.value === 'auditoria' ? 'auditoria' : undefined,
    start_date: filters.value.start_date || undefined,
    end_date: filters.value.end_date || undefined,
    transaction_kind: filters.value.transaction_kind || undefined,
    public_kind: filters.value.public_kind || undefined,
    q: filters.value.q || undefined,
    limit:
      Number(filters.value.limit || 30) === 30
        ? undefined
        : filters.value.limit,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

const syncAuditRouteState = () => {
  const nextQuery = compactQuery({
    ...route.query,
    view: 'auditoria',
    start_date: auditFilters.value.start_date || undefined,
    end_date: auditFilters.value.end_date || undefined,
    actor_id: auditFilters.value.actor_id || undefined,
    action_type: auditFilters.value.action_type || undefined,
    q: auditFilters.value.q || undefined,
    page: auditFilters.value.page > 1 ? auditFilters.value.page : undefined,
    transaction_kind: undefined,
    public_kind: undefined,
    limit: undefined,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

const loadHistory = async () => {
  loading.value = true;
  syncHistoryRouteState();
  try {
    const { data } = await HistoryAPI.show(cleanParams());
    payload.value = {
      periodo: { label: '-' },
      cards: {
        saldo: null,
        receita: null,
        despesa: null,
        comissao: null,
      },
      transacoes: [],
      historico_publico: [],
      atividades: [],
      resumo_publico: {
        total: 0,
        tickets: 0,
        feedback_suporte: 0,
        sessoes_responde: 0,
        visitas_publicas: 0,
        incidentes_status: 0,
      },
      ...data,
    };
  } catch {
    useAlert('Não foi possível carregar o histórico da conta.');
  } finally {
    loading.value = false;
  }
};

const auditParams = extra =>
  cleanObject({
    ...auditFilters.value,
    ...extra,
  });

const loadAudit = async (page = auditFilters.value.page) => {
  if (!isAdmin.value) {
    activeView.value = 'operacional';
    await loadHistory();
    return;
  }

  auditLoading.value = true;
  auditFilters.value.page = page;
  syncAuditRouteState();
  try {
    const { data } = await AuditLogsAPI.get(auditParams({ page }));
    auditPayload.value = {
      audit_logs_enabled: true,
      audit_logs: [],
      current_page: 1,
      total_entries: 0,
      per_page: 15,
      ...data,
    };
    track('audit_trail_filtered', {
      has_date: Boolean(
        auditFilters.value.start_date || auditFilters.value.end_date
      ),
      has_actor: Boolean(auditFilters.value.actor_id),
      action_type: auditFilters.value.action_type || 'all',
      results_count: auditPayload.value.total_entries,
    });
  } catch {
    useAlert('Não foi possível carregar a trilha de auditoria da conta.');
  } finally {
    auditLoading.value = false;
  }
};

const setView = async view => {
  activeView.value = normalizeView(view);
  if (activeView.value === 'auditoria') {
    track('audit_trail_viewed', {
      account_id: route.params.accountId,
    });
    await loadAudit(1);
  } else {
    await loadHistory();
  }
};

const refreshCurrentView = () =>
  activeView.value === 'auditoria' ? loadAudit() : loadHistory();

const clearFilters = async () => {
  filters.value = {
    start_date: '',
    end_date: '',
    transaction_kind: '',
    public_kind: '',
    q: '',
    limit: 30,
  };
  await loadHistory();
};

const clearAuditFilters = async () => {
  auditFilters.value = {
    start_date: '',
    end_date: '',
    actor_id: '',
    action_type: '',
    q: '',
    page: 1,
  };
  await loadAudit(1);
};

const actionMeta = action =>
  ACTION_META[action] || {
    label: action || 'Ação',
    cls: 'bg-slate-100 text-slate-700',
  };

const auditableLabel = type => AUDITABLE_LABELS[type] || type || 'Entidade';

const actorLabel = audit =>
  audit.username || agentsById.value[audit.user_id]?.name || 'Sistema';

const auditDescription = audit => {
  const keys =
    audit.audited_changes && typeof audit.audited_changes === 'object'
      ? Object.keys(audit.audited_changes).filter(
          key => !SENSITIVE_AUDIT_FIELDS.some(field => key.includes(field))
        )
      : [];
  const entity = `${auditableLabel(audit.auditable_type)} #${audit.auditable_id}`;
  if (!keys.length) return entity;

  return `${entity} · ${keys.slice(0, 3).join(', ')}`;
};

const maskAuditValue = (field, value) =>
  SENSITIVE_AUDIT_FIELDS.some(item => field.includes(item))
    ? '[mascarado]'
    : JSON.stringify(value ?? '');

const auditChangeRows = audit => {
  const changes = audit?.audited_changes || {};
  return Object.entries(changes).map(([field, value]) => {
    let before = null;
    let after = null;
    if (Array.isArray(value)) {
      [before, after] = value;
    } else if (audit.action === 'destroy') {
      before = value;
    } else if (audit.action === 'create') {
      after = value;
    }
    return {
      field,
      before: maskAuditValue(field, before),
      after: maskAuditValue(field, after),
    };
  });
};

const csvEscape = value => `"${String(value ?? '').replaceAll('"', '""')}"`;

const openAuditDiff = audit => {
  selectedAudit.value = audit;
  track('audit_trail_diff_opened', {
    auditable_type: audit.auditable_type,
    action: audit.action,
  });
};

const exportAuditCsv = async () => {
  auditExporting.value = true;
  try {
    const rows = [
      ['Data', 'Usuário', 'Ação', 'Entidade', 'Descrição', 'Alterações'],
    ];

    const appendAuditRows = logs => {
      logs.forEach(audit => {
        rows.push([
          formatDate(audit.created_at),
          actorLabel(audit),
          actionMeta(audit.action).label,
          auditableLabel(audit.auditable_type),
          auditDescription(audit),
          auditChangeRows(audit)
            .map(row => `${row.field}: ${row.before} -> ${row.after}`)
            .join('; '),
        ]);
      });
    };

    const firstPage = await AuditLogsAPI.get(auditParams({ page: 1 }));
    const firstData = firstPage.data || {};
    const firstLogs = firstData.audit_logs || [];
    const totalPages = Math.max(
      1,
      Math.ceil(
        (firstData.total_entries || firstLogs.length) /
          (firstData.per_page || 15)
      )
    );
    appendAuditRows(firstLogs);

    if (totalPages > 1) {
      const remainingResponses = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          AuditLogsAPI.get(auditParams({ page: index + 2 }))
        )
      );
      remainingResponses.forEach(({ data }) => {
        const logs = data.audit_logs || [];
        appendAuditRows(logs);
      });
    }

    const blob = new Blob(
      [rows.map(row => row.map(csvEscape).join(',')).join('\n')],
      { type: 'text/csv;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trilha-auditoria-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    track('audit_trail_exported', {
      rows: rows.length - 1,
      action_type: auditFilters.value.action_type || 'all',
      has_filters: Boolean(
        auditFilters.value.start_date ||
          auditFilters.value.end_date ||
          auditFilters.value.actor_id ||
          auditFilters.value.action_type ||
          auditFilters.value.q
      ),
    });
  } catch {
    useAlert('Não foi possível exportar a trilha de auditoria.');
  } finally {
    auditExporting.value = false;
  }
};

const auditTotalPages = computed(() =>
  Math.max(
    1,
    Math.ceil(
      (auditPayload.value.total_entries || 0) /
        (auditPayload.value.per_page || 15)
    )
  )
);
const hasAuditFilters = computed(() =>
  Boolean(
    auditFilters.value.start_date ||
      auditFilters.value.end_date ||
      auditFilters.value.actor_id ||
      auditFilters.value.action_type ||
      auditFilters.value.q
  )
);

onMounted(async () => {
  store.dispatch('agents/get');
  if (activeView.value === 'auditoria') {
    await loadAudit();
  } else {
    await loadHistory();
  }
});
</script>

<template>
  <section :class="LAIS_PAGE_SCROLL_SHELL_CLASS">
    <LaisHeader
      title="Histórico da conta"
      helper="Acompanhe movimentações, atendimentos, visitas e atividades recentes da sua conta."
    >
      <template #actions>
        <div
          class="inline-flex rounded-[14px] border border-[#d9d2e6] bg-white p-1"
        >
          <button
            type="button"
            class="h-9 rounded-[10px] px-3 text-sm font-semibold transition"
            :class="
              activeView === 'operacional'
                ? 'bg-lais-primary text-white'
                : 'text-lais-primary hover:bg-[#f7f4fb]'
            "
            @click="setView('operacional')"
          >
            Histórico
          </button>
          <button
            v-if="isAdmin"
            type="button"
            class="h-9 rounded-[10px] px-3 text-sm font-semibold transition"
            :class="
              activeView === 'auditoria'
                ? 'bg-lais-primary text-white'
                : 'text-lais-primary hover:bg-[#f7f4fb]'
            "
            @click="setView('auditoria')"
          >
            Auditoria
          </button>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-[14px] bg-lais-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-lais-primary-600"
          @click="refreshCurrentView"
        >
          Atualizar
        </button>
      </template>
    </LaisHeader>

    <div :class="LAIS_PAGE_PADDING_CLASS">
      <template v-if="activeView === 'operacional'">
        <div
          class="mb-5 grid gap-3 rounded-lais-xl border border-[#e7ddf4] bg-white p-4 shadow-[0_10px_30px_rgba(73,49,122,0.06)] md:grid-cols-2 xl:grid-cols-6"
        >
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Busca
            <input
              v-model="filters.q"
              type="search"
              placeholder="Buscar evento, ação ou detalhe"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Início
            <input
              v-model="filters.start_date"
              type="date"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Fim
            <input
              v-model="filters.end_date"
              type="date"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Tipo financeiro
            <select
              v-model="filters.transaction_kind"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            >
              <option value="">Todos</option>
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
              <option value="comissao">Comissão</option>
            </select>
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Tipo de interação
            <select
              v-model="filters.public_kind"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            >
              <option value="">Todos</option>
              <option value="ticket">Atendimento</option>
              <option value="feedback">Feedback</option>
              <option value="responde">Responde</option>
              <option value="visita">Visita</option>
              <option value="status">Status</option>
            </select>
          </label>
          <div class="flex flex-col justify-end gap-2 sm:flex-row xl:flex-col">
            <button
              type="button"
              class="h-11 rounded-[14px] bg-lais-primary px-4 text-sm font-semibold text-white transition hover:bg-[#3b2862]"
              @click="loadHistory"
            >
              Aplicar filtros
            </button>
            <button
              type="button"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-4 text-sm font-semibold text-lais-primary transition hover:bg-[#f7f4fb]"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
        </div>

        <div class="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <LaisCard
            v-for="(value, key) in payload.cards"
            :key="key"
            density="tight"
          >
            <div class="flex flex-col gap-2">
              <span
                class="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#9aa3b2]"
              >
                {{ cardLabel(key) }}
              </span>
              <strong
                class="text-[28px] font-semibold leading-none text-lais-text font-redHatDisplay"
              >
                {{ formatBRL(value) }}
              </strong>
            </div>
          </LaisCard>
        </div>

        <div class="mb-5 grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <LaisCard title="Movimentações financeiras">
            <div
              class="mb-4 flex flex-wrap items-center gap-2 text-[12px] text-[#667085]"
            >
              <span
                class="rounded-full bg-[#f7f4fb] px-3 py-1 font-semibold text-lais-primary"
              >
                {{ payload.periodo.label }}
              </span>
              <span class="rounded-full bg-[#f1f5f9] px-3 py-1">
                {{
                  formatCount(
                    payload.transacoes.length,
                    'movimentação',
                    'movimentações'
                  )
                }}
              </span>
            </div>
            <div v-if="loading" class="py-10 text-center text-[#667085]">
              Carregando histórico...
            </div>
            <div
              v-else-if="!payload.transacoes.length"
              class="py-10 text-center text-[#667085]"
            >
              Nenhuma movimentação encontrada.
            </div>
            <div v-else class="space-y-3">
              <article
                v-for="item in payload.transacoes"
                :key="item.id"
                class="rounded-[18px] border border-[#ebe5f4] bg-[#fcfbfe] p-4"
              >
                <div
                  class="mb-2 flex flex-wrap items-start justify-between gap-3"
                >
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                        :class="kindMeta(item.kind).cls"
                      >
                        {{ kindMeta(item.kind).label }}
                      </span>
                      <span
                        class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                        :class="statusMeta(item.status).cls"
                      >
                        {{ statusMeta(item.status).label }}
                      </span>
                    </div>
                    <h3
                      class="mt-2 text-[16px] font-semibold text-lais-text font-redHatDisplay"
                    >
                      {{ item.title }}
                    </h3>
                  </div>
                  <strong class="text-[18px] text-lais-text">
                    {{ formatBRL(item.amount) }}
                  </strong>
                </div>
                <p class="text-[14px] leading-6 text-[#667085]">
                  {{ item.description || 'Sem descrição adicional.' }}
                </p>
                <div
                  class="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-[#98a2b3]"
                >
                  <span>{{
                    item.date_label || formatDate(item.occurred_at)
                  }}</span>
                  <a
                    v-if="item.link_url"
                    :href="item.link_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-semibold text-lais-primary"
                  >
                    Abrir detalhe
                  </a>
                </div>
              </article>
            </div>
          </LaisCard>

          <LaisCard title="Resumo de interações" density="tight">
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div class="rounded-[18px] bg-[#f7f4fb] p-4">
                <div
                  class="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#9aa3b2]"
                >
                  Interações registradas
                </div>
                <div
                  class="mt-2 text-[30px] font-semibold text-lais-text font-redHatDisplay"
                >
                  {{ payload.resumo_publico.total }}
                </div>
              </div>
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div class="rounded-[18px] border border-[#ebe5f4] p-4">
                  Atendimentos: {{ payload.resumo_publico.tickets }}
                </div>
                <div class="rounded-[18px] border border-[#ebe5f4] p-4">
                  Feedbacks: {{ payload.resumo_publico.feedback_suporte }}
                </div>
                <div class="rounded-[18px] border border-[#ebe5f4] p-4">
                  Conversas no Responde:
                  {{ payload.resumo_publico.sessoes_responde }}
                </div>
                <div class="rounded-[18px] border border-[#ebe5f4] p-4">
                  Visitas: {{ payload.resumo_publico.visitas_publicas }}
                </div>
                <div class="rounded-[18px] border border-[#ebe5f4] p-4">
                  Avisos de status:
                  {{ payload.resumo_publico.incidentes_status }}
                </div>
              </div>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-4 lg:grid-cols-2">
          <LaisCard title="Interações registradas">
            <div
              v-if="!payload.historico_publico.length"
              class="py-10 text-center text-[#667085]"
            >
              Nenhuma interação encontrada.
            </div>
            <div v-else class="space-y-3">
              <article
                v-for="item in payload.historico_publico"
                :key="item.id"
                class="rounded-[18px] border border-[#ebe5f4] bg-white p-4"
              >
                <div class="mb-2 flex flex-wrap items-center gap-2">
                  <span
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    :class="kindMeta(item.kind).cls"
                  >
                    {{ kindMeta(item.kind).label }}
                  </span>
                  <span
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    :class="statusMeta(item.status).cls"
                  >
                    {{ statusMeta(item.status).label }}
                  </span>
                </div>
                <h3
                  class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
                >
                  {{ item.title }}
                </h3>
                <p class="mt-2 text-[14px] leading-6 text-[#667085]">
                  {{ item.description || 'Sem descrição adicional.' }}
                </p>
                <div
                  class="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-[#98a2b3]"
                >
                  <span>{{
                    item.date_label || formatDate(item.occurred_at)
                  }}</span>
                  <a
                    v-if="item.link_url"
                    :href="item.link_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-semibold text-lais-primary"
                  >
                    Abrir detalhe
                  </a>
                </div>
              </article>
            </div>
          </LaisCard>

          <LaisCard title="Atividades da conta">
            <div
              v-if="!payload.atividades.length"
              class="py-10 text-center text-[#667085]"
            >
              Nenhuma atividade encontrada.
            </div>
            <div v-else class="space-y-3">
              <article
                v-for="item in payload.atividades"
                :key="item.id"
                class="rounded-[18px] border border-[#ebe5f4] bg-white p-4"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <h3
                      class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
                    >
                      {{ item.action }}
                    </h3>
                    <p class="mt-2 text-[14px] leading-6 text-[#667085]">
                      {{ item.description }}
                    </p>
                  </div>
                  <span
                    class="rounded-full bg-[#f7f4fb] px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
                  >
                    {{ item.actor }}
                  </span>
                </div>
                <div class="mt-3 text-[12px] text-[#98a2b3]">
                  {{ item.date_label || formatDate(item.occurred_at) }}
                </div>
              </article>
            </div>
          </LaisCard>
        </div>
      </template>

      <template v-else>
        <div
          class="mb-5 grid gap-3 rounded-lais-xl border border-[#e7ddf4] bg-white p-4 shadow-[0_10px_30px_rgba(73,49,122,0.06)] md:grid-cols-2 xl:grid-cols-6"
        >
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Buscar
            <input
              v-model="auditFilters.q"
              type="search"
              placeholder="Usuário, entidade ou ID"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
              @keyup.enter="loadAudit(1)"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Início
            <input
              v-model="auditFilters.start_date"
              type="date"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Fim
            <input
              v-model="auditFilters.end_date"
              type="date"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            />
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Usuário
            <select
              v-model="auditFilters.actor_id"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            >
              <option value="">Todos os usuários</option>
              <option value="system">Sistema</option>
              <option
                v-for="agent in agents"
                :key="agent.id"
                :value="String(agent.id)"
              >
                {{ agent.name }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-2 text-sm text-lais-text-body">
            Tipo de ação
            <select
              v-model="auditFilters.action_type"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-3 text-lais-text outline-none transition focus:border-lais-primary"
            >
              <option value="">Todas as ações</option>
              <option value="create">Criação</option>
              <option value="update">Atualização</option>
              <option value="destroy">Exclusão</option>
            </select>
          </label>
          <div class="flex flex-col justify-end gap-2 sm:flex-row xl:flex-col">
            <button
              type="button"
              class="h-11 rounded-[14px] bg-lais-primary px-4 text-sm font-semibold text-white transition hover:bg-[#3b2862]"
              @click="loadAudit(1)"
            >
              Filtrar
            </button>
            <button
              type="button"
              class="h-11 rounded-[14px] border border-[#d9d2e6] px-4 text-sm font-semibold text-lais-primary transition hover:bg-[#f7f4fb]"
              @click="clearAuditFilters"
            >
              Limpar
            </button>
          </div>
        </div>

        <LaisCard title="Trilha de auditoria">
          <template #titleAction>
            <button
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lais-lg border border-[#d9d2e6] px-3 text-sm font-semibold text-lais-primary transition hover:bg-[#f7f4fb] disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="
                auditExporting ||
                auditLoading ||
                !auditPayload.audit_logs_enabled ||
                auditPayload.total_entries === 0
              "
              @click="exportAuditCsv"
            >
              <span
                class="size-4"
                :class="
                  auditExporting
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-download'
                "
                aria-hidden="true"
              />
              {{ auditExporting ? 'Exportando…' : 'Exportar' }}
            </button>
          </template>

          <div
            v-if="!auditPayload.audit_logs_enabled"
            class="rounded-[18px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          >
            A trilha de auditoria não está habilitada para esta conta.
          </div>
          <div
            v-else-if="auditLoading"
            class="py-12 text-center text-[#667085]"
          >
            Carregando trilha de auditoria…
          </div>
          <div
            v-else-if="!auditPayload.audit_logs.length"
            class="py-12 text-center text-[#667085]"
          >
            <p>
              {{
                hasAuditFilters
                  ? 'Nenhuma ação encontrada para os filtros selecionados.'
                  : 'Sem atividades registradas'
              }}
            </p>
            <button
              v-if="hasAuditFilters"
              type="button"
              class="mt-3 text-sm font-semibold text-lais-primary"
              @click="clearAuditFilters"
            >
              Limpar filtros
            </button>
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-[920px] w-full text-left text-sm">
              <thead>
                <tr class="border-b border-[#ebe5f4] text-lais-text-body">
                  <th class="py-3 pr-3 font-semibold">Data</th>
                  <th class="py-3 pr-3 font-semibold">Usuário</th>
                  <th class="py-3 pr-3 font-semibold">Ação</th>
                  <th class="py-3 pr-3 font-semibold">Descrição</th>
                  <th class="py-3 text-right font-semibold">Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="audit in auditPayload.audit_logs"
                  :key="audit.id"
                  class="border-b border-[#f3eef8] hover:bg-[#fcfbfe]"
                >
                  <td class="py-3 pr-3 text-lais-text-body">
                    {{ formatDate(audit.created_at) }}
                  </td>
                  <td class="py-3 pr-3 font-semibold text-lais-text">
                    {{ actorLabel(audit) }}
                  </td>
                  <td class="py-3 pr-3">
                    <span
                      class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      :class="actionMeta(audit.action).cls"
                    >
                      {{ actionMeta(audit.action).label }}
                    </span>
                    <span class="ml-2 text-xs text-[#667085]">
                      {{ auditableLabel(audit.auditable_type) }}
                    </span>
                  </td>
                  <td class="py-3 pr-3 text-lais-text-body">
                    {{ auditDescription(audit) }}
                  </td>
                  <td class="py-3 text-right">
                    <button
                      type="button"
                      class="inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-[#d9d2e6] px-3 text-xs font-semibold text-lais-primary hover:bg-[#f7f4fb]"
                      @click="openAuditDiff(audit)"
                    >
                      <span
                        class="i-lucide-git-compare size-3.5"
                        aria-hidden="true"
                      />
                      Ver alterações
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              class="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-lais-text-body"
            >
              <span>
                {{ auditPayload.total_entries }} registro(s) · página
                {{ auditPayload.current_page }} de {{ auditTotalPages }}
              </span>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="h-9 rounded-[10px] border border-[#d9d2e6] px-3 font-semibold disabled:opacity-50"
                  :disabled="auditPayload.current_page <= 1"
                  @click="loadAudit(auditPayload.current_page - 1)"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  class="h-9 rounded-[10px] border border-[#d9d2e6] px-3 font-semibold disabled:opacity-50"
                  :disabled="auditPayload.current_page >= auditTotalPages"
                  @click="loadAudit(auditPayload.current_page + 1)"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        </LaisCard>
      </template>
    </div>

    <LaisModal
      :show="Boolean(selectedAudit)"
      title="Alterações registradas"
      max-width-class="max-w-[720px]"
      @close="selectedAudit = null"
    >
      <div v-if="selectedAudit" class="space-y-4">
        <div
          class="rounded-[18px] bg-[#f7f4fb] p-4 text-sm text-lais-text-body"
        >
          <div class="font-semibold text-lais-text">
            {{ actionMeta(selectedAudit.action).label }} ·
            {{ auditableLabel(selectedAudit.auditable_type) }}
            #{{ selectedAudit.auditable_id }}
          </div>
          <div class="mt-1">
            {{ actorLabel(selectedAudit) }} ·
            {{ formatDate(selectedAudit.created_at) }}
          </div>
        </div>
        <div
          v-if="!auditChangeRows(selectedAudit).length"
          class="py-6 text-center text-[#667085]"
        >
          Sem campos alterados registrados para esta ação.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-[600px] w-full text-left text-sm">
            <thead>
              <tr class="border-b border-[#ebe5f4] text-lais-text-body">
                <th class="py-2 pr-3 font-semibold">Campo</th>
                <th class="py-2 pr-3 font-semibold">Antes</th>
                <th class="py-2 font-semibold">Depois</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in auditChangeRows(selectedAudit)"
                :key="row.field"
                class="border-b border-[#f3eef8]"
              >
                <td class="py-3 pr-3 font-semibold text-lais-text">
                  {{ row.field }}
                </td>
                <td class="py-3 pr-3 text-rose-700">
                  {{ row.before }}
                </td>
                <td class="py-3 text-emerald-700">
                  {{ row.after }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </LaisModal>
  </section>
</template>
